import { computed } from '@angular/core';
import { patchState, signalStore, withState, withComputed, withMethods } from '@ngrx/signals';
import { AuthUser, AuthCompany, AuthSession, UserProfile } from '../interfaces';

interface AuthState {
  user: AuthUser | null;
  userProfile: UserProfile | null;
  company: AuthCompany | null;
  idToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isAuthenticated: boolean;
  requiresPasswordChange: boolean;
}

const initialState: AuthState = {
  user: null,
  userProfile: null,
  company: null,
  idToken: null,
  refreshToken: null,
  expiresAt: null,
  isAuthenticated: false,
  requiresPasswordChange: false,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    userDisplayName: computed(() => store.userProfile()?.full_name ?? store.user()?.displayName ?? ''),
    userEmail: computed(() => store.userProfile()?.email ?? store.user()?.email ?? ''),
    userRoleNames: computed(() => {
      const profile = store.userProfile();
      if (profile) return profile.role_name ? [profile.role_name] : [];
      return store.user()?.roleNames ?? [];
    }),
    userPermissions: computed(() => {
      const profile = store.userProfile();
      if (profile) return profile.permissions.map(p => p.code);
      return store.user()?.permissions ?? [];
    }),
    companyName: computed(() => store.company()?.name ?? ''),
    companyLogo: computed(() => store.company()?.logoUrl ?? null),
    isTokenExpired: computed(() => {
      const expiresAt = store.expiresAt();
      return expiresAt !== null && Date.now() >= expiresAt;
    }),
  })),
  withMethods((store) => ({
    setSession(session: AuthSession): void {
      patchState(store, {
        user: session.user,
        company: session.company,
        idToken: session.idToken,
        refreshToken: session.refreshToken,
        expiresAt: session.expiresAt,
        isAuthenticated: true,
        requiresPasswordChange: session.requiresPasswordChange,
      });
    },
    setUserProfile(profile: UserProfile): void {
      patchState(store, { userProfile: profile });
    },
    /** Renovación de token: conserva usuario, empresa y perfil ya cargados. */
    updateTokens(idToken: string, refreshToken: string, expiresAt: number): void {
      patchState(store, { idToken, refreshToken, expiresAt });
    },
    clearPasswordChangeFlag(): void {
      patchState(store, { requiresPasswordChange: false });
    },
    clearSession(): void {
      patchState(store, {
        user: null,
        userProfile: null,
        company: null,
        idToken: null,
        refreshToken: null,
        expiresAt: null,
        isAuthenticated: false,
        requiresPasswordChange: false,
      });
    },
  })),
);
